import React, { useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';

/**
 * Client Help Page
 * Provides step-by-step instructions for clients on how to use the platform
 */

const ClientHelpPage = () => {
  const [activeSection, setActiveSection] = useState('getting-started');

  const sections = [
    { id: 'getting-started', name: 'Getting Started' },
    { id: 'managing-trainers', name: 'Managing Trainers' },
    { id: 'managing-sessions', name: 'Managing Sessions' },
    { id: 'reports-analytics', name: 'Reports & Analytics' },
  ];

  // Step card component
  const StepCard = ({ number, title, description, tips }) => (
    <div className="bg-white border border-neutral-200 rounded-xl p-3 sm:p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-bold text-sm sm:text-lg flex-shrink-0">
          {number}
        </div>
        <div className="flex-1">
          <h3 className="text-sm sm:text-lg font-semibold text-neutral-900 mb-1 sm:mb-2">{title}</h3>
          <p className="text-xs sm:text-sm text-neutral-600 mb-2 sm:mb-3">{description}</p>
          {tips && tips.length > 0 && (
            <div className="bg-amber-50 rounded-lg p-2 sm:p-3 border border-amber-200">
              <p className="text-[10px] sm:text-xs font-semibold text-amber-800 mb-1">Tips:</p>
              <ul className="text-[10px] sm:text-xs text-amber-700 space-y-1">
                {tips.map((tip, index) => (
                  <li key={index}>• {tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="p-4 lg:p-6 pb-20 lg:pb-6">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-neutral-900 mb-2">
              Help
            </h1>
            <p className="text-xs sm:text-sm text-neutral-600">
              Learn how to use Aktivity to manage your training sessions, trainers, and track performance.
            </p>
          </div>

          {/* Quick Overview Card */}
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl p-4 sm:p-6 mb-8 border border-primary-200">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-primary-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h2 className="text-base sm:text-lg font-semibold text-primary-900 mb-2">Client Dashboard Overview</h2>
                <ul className="text-xs sm:text-sm text-primary-800 space-y-1">
                  <li>• Manage your organization's trainers and sessions</li>
                  <li>• Create and schedule training sessions</li>
                  <li>• Monitor live sessions and view real-time engagement</li>
                  <li>• Access comprehensive reports and analytics</li>
                  <li>• Track trainer performance across sessions</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Section Navigation */}
          <div className="flex flex-wrap gap-2 mb-8">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                  activeSection === section.id
                    ? 'bg-primary-500 text-white'
                    : 'bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50'
                }`}
              >
                {section.name}
              </button>
            ))}
          </div>

          {/* Getting Started Section */}
          {activeSection === 'getting-started' && (
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-xl p-3 sm:p-4 mb-6 border border-blue-200">
                <h2 className="text-base sm:text-lg font-semibold text-blue-900 mb-2">Getting Started</h2>
                <p className="text-xs sm:text-sm text-blue-800">
                  Complete these initial steps to set up your organization and start using Aktivity effectively.
                </p>
              </div>

              <StepCard
                number={1}
                title="Explore Your Dashboard"
                description="After logging in, you'll land on your dashboard. Here you can see an overview of your organization's training activities, upcoming sessions, and key metrics."
                tips={[
                  "The dashboard shows quick stats and recent activity",
                  "Use the sidebar to navigate to different sections",
                  "Check notifications for important updates"
                ]}
              />

              <StepCard
                number={2}
                title="Review Your Subscription"
                description="Go to 'Subscription' in the sidebar to view your current plan, usage limits, and available features. You can upgrade anytime to unlock more capabilities."
                tips={[
                  "Free plan includes basic features to get started",
                  "Pro plan unlocks unlimited trainers and sessions",
                  "Check your usage against plan limits regularly"
                ]}
              />

              <StepCard
                number={3}
                title="Add Your First Trainer"
                description="Navigate to 'Trainers' and click 'Add Trainer' to invite trainers to your organization. Trainers will receive an email invitation to join."
                tips={[
                  "Trainers need a valid email address",
                  "You can add multiple trainers based on your plan",
                  "Trainers can manage their own sessions once added"
                ]}
              />

              <StepCard
                number={4}
                title="Create Your First Session"
                description="Go to 'Sessions' and click 'Create Session' to set up your first training session. Fill in the session details, assign a trainer, and set the schedule."
                tips={[
                  "Choose a clear, descriptive session name",
                  "Set realistic participant limits",
                  "Assign the appropriate trainer for the topic"
                ]}
              />

              <StepCard
                number={5}
                title="Understand the Terminology"
                description="Visit the 'Terminology' page to learn about the metrics and scores used in Aktivity. Understanding ARS, TEI, TRS, and other metrics will help you interpret reports."
                tips={[
                  "Metrics help measure training effectiveness",
                  "Each score has interpretation bands (high, moderate, low)",
                  "Use metrics to identify areas for improvement"
                ]}
              />
            </div>
          )}

          {/* Managing Trainers Section */}
          {activeSection === 'managing-trainers' && (
            <div className="space-y-4">
              <div className="bg-green-50 rounded-xl p-3 sm:p-4 mb-6 border border-green-200">
                <h2 className="text-base sm:text-lg font-semibold text-green-900 mb-2">Managing Trainers</h2>
                <p className="text-xs sm:text-sm text-green-800">
                  Learn how to add, manage, and track your organization's trainers effectively.
                </p>
              </div>

              <StepCard
                number={1}
                title="Add New Trainers"
                description="From the Trainers page, click 'Add Trainer' to invite a new trainer. Enter their name, email, and optionally their specialization areas."
                tips={[
                  "Trainers receive an email with login instructions",
                  "They can set their own password on first login",
                  "Consider adding trainer specializations for easier assignment"
                ]}
              />

              <StepCard
                number={2}
                title="View Trainer Profiles"
                description="Click on any trainer's name to view their detailed profile, including their session history, performance metrics, and feedback scores."
                tips={[
                  "TEI (Trainer Effectiveness Index) shows overall effectiveness",
                  "LAI (Learning Advocacy Index) shows recommendation likelihood",
                  "Review feedback to identify training strengths"
                ]}
              />

              <StepCard
                number={3}
                title="Track Trainer Performance"
                description="Monitor each trainer's metrics over time. Look at their average scores, session completion rates, and participant feedback."
                tips={[
                  "Compare metrics across different trainers",
                  "Identify top performers and areas for coaching",
                  "Use insights for professional development"
                ]}
              />

              <StepCard
                number={4}
                title="Assign Trainers to Sessions"
                description="When creating or editing a session, select the appropriate trainer from your team. Consider their expertise and availability."
                tips={[
                  "Match trainer expertise with session topics",
                  "Check trainer availability before assigning",
                  "A trainer can be reassigned if needed"
                ]}
              />

              <StepCard
                number={5}
                title="Review Facilitation Feedback"
                description="After sessions, review the facilitation feedback to understand how trainers performed. This feedback is based on participant responses."
                tips={[
                  "Feedback includes specific areas like clarity and engagement",
                  "Share constructive feedback with trainers",
                  "Use feedback for continuous improvement"
                ]}
              />
            </div>
          )}

          {/* Managing Sessions Section */}
          {activeSection === 'managing-sessions' && (
            <div className="space-y-4">
              <div className="bg-purple-50 rounded-xl p-3 sm:p-4 mb-6 border border-purple-200">
                <h2 className="text-base sm:text-lg font-semibold text-purple-900 mb-2">Managing Sessions</h2>
                <p className="text-xs sm:text-sm text-purple-800">
                  Create, schedule, and monitor training sessions for your organization.
                </p>
              </div>

              <StepCard
                number={1}
                title="Create a New Session"
                description="Click 'Create Session' from the Sessions page. Fill in the session name, description, date, time, and select a trainer. Set the participant limit and any other preferences."
                tips={[
                  "Choose descriptive names for easy identification",
                  "Set realistic time durations",
                  "Consider time zones if participants are remote"
                ]}
              />

              <StepCard
                number={2}
                title="Configure Session Settings"
                description="Customize session settings including participant registration requirements, workbook assignments, and notification preferences."
                tips={[
                  "Enable pre-session challenges for better engagement",
                  "Assign relevant workbooks for reference materials",
                  "Set up reminders for participants"
                ]}
              />

              <StepCard
                number={3}
                title="Share Session Code"
                description="Each session has a unique code that participants use to join. Share this code through your preferred communication channels."
                tips={[
                  "The session code is displayed on the session details page",
                  "Participants can join via the landing page or direct link",
                  "QR codes can also be used for easy joining"
                ]}
              />

              <StepCard
                number={4}
                title="Monitor Live Sessions"
                description="While a session is in progress, you can view real-time engagement metrics, participant count, and activity completion rates."
                tips={[
                  "The dashboard shows currently active sessions",
                  "Monitor engagement rates during the session",
                  "Trainers manage the session flow and activities"
                ]}
              />

              <StepCard
                number={5}
                title="Review Session Status"
                description="Track session status from 'Scheduled' to 'In Progress' to 'Completed'. Each status change is reflected in your sessions list."
                tips={[
                  "Scheduled sessions appear with upcoming dates",
                  "In Progress sessions show live indicators",
                  "Completed sessions have full reports available"
                ]}
              />

              <StepCard
                number={6}
                title="Edit or Cancel Sessions"
                description="You can edit session details or cancel sessions before they start. Navigate to the session and use the edit or cancel options."
                tips={[
                  "Notify participants if you make changes",
                  "Consider rescheduling instead of canceling",
                  "Past sessions cannot be edited"
                ]}
              />
            </div>
          )}

          {/* Reports & Analytics Section */}
          {activeSection === 'reports-analytics' && (
            <div className="space-y-4">
              <div className="bg-amber-50 rounded-xl p-3 sm:p-4 mb-6 border border-amber-200">
                <h2 className="text-base sm:text-lg font-semibold text-amber-900 mb-2">Reports & Analytics</h2>
                <p className="text-xs sm:text-sm text-amber-800">
                  Access comprehensive reports and analytics to measure training effectiveness.
                </p>
              </div>

              <StepCard
                number={1}
                title="View Session Reports"
                description="After a session completes, access its detailed report from the Sessions page. Click on the session and select 'View Report' to see comprehensive metrics."
                tips={[
                  "Reports include engagement, feedback, and action readiness scores",
                  "View individual participant performance",
                  "Export reports for sharing with stakeholders"
                ]}
              />

              <StepCard
                number={2}
                title="Understand Key Metrics"
                description="Learn what each metric means: ARS (Action Readiness Score), TEI (Trainer Effectiveness Index), TRS (Topic Relevance Score), and more."
                tips={[
                  "Visit the Terminology page for detailed explanations",
                  "Higher scores indicate better outcomes",
                  "Compare metrics across sessions for trends"
                ]}
              />

              <StepCard
                number={3}
                title="Access Group Reports"
                description="Group reports show aggregated data across multiple participants. Use these to identify common challenges and successful strategies."
                tips={[
                  "Group reports help identify training gaps",
                  "Look for patterns in challenges submitted",
                  "Use insights to improve future sessions"
                ]}
              />

              <StepCard
                number={4}
                title="View Analytics Dashboard"
                description="The Analytics page provides organization-wide insights including total sessions, participant engagement trends, and trainer performance comparisons."
                tips={[
                  "Filter analytics by date range",
                  "Compare performance across time periods",
                  "Identify your most effective sessions"
                ]}
              />

              <StepCard
                number={5}
                title="Review Participant Details"
                description="Drill down into individual participant performance from any session report. See their activity completion, feedback submitted, and action plans."
                tips={[
                  "Identify highly engaged participants",
                  "Review action plans for follow-up opportunities",
                  "Track individual progress across sessions"
                ]}
              />

              <StepCard
                number={6}
                title="Export and Share Reports"
                description="Export reports in various formats for sharing with stakeholders, leadership, or for record-keeping purposes."
                tips={[
                  "PDF exports are great for sharing",
                  "Include relevant metrics for your audience",
                  "Schedule regular reporting for stakeholders"
                ]}
              />
            </div>
          )}

          {/* Help Section */}
          <div className="mt-8 bg-neutral-100 rounded-xl p-4 sm:p-6 border border-neutral-200">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-neutral-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="text-sm sm:text-base font-semibold text-neutral-900 mb-2">Need More Help?</h3>
                <p className="text-xs sm:text-sm text-neutral-600 mb-3">
                  If you have questions or need assistance, our support team is here to help.
                  You can also check the Terminology page for detailed metric explanations.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white border border-neutral-300 text-neutral-700">
                    support@aktivity.com
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ClientHelpPage;
